import React from 'react';

type ActionItem = {
  id: string;
  label: string;
  priority?: string;
  status?: string;
  timeframe?: string | null;
  dueDate?: string | null;
  metric?: string | null;
};

type ActionPlan = {
  primaryFocus: ActionItem | null;
  supporting: ActionItem[];
  reflectionPrompt: string;
  progress: { completed: number; total: number };
};

type KeyInsight = {
  pattern: string;
  impact: string;
  shift: string;
};

type Experiment = {
  patternIdentified: string;
  costOfContinuing: string;
  experiment30d: string;
} | null;

type Framework = {
  when: string;
  do: string;
  avoid: string;
} | null;

type Momentum = {
  sinceLastMonth?: { clarity: 'up'|'down'|'steady'; overwhelm: 'up'|'down'|'steady'; focus: 'up'|'down'|'steady' };
  changes?: { stopped?: string; started?: string; strengthening?: string };
} | null;

type InsightsV2Data = {
  action_plan?: ActionPlan;
  key_insights?: KeyInsight[];
  experiment?: Experiment;
  framework?: Framework;
  momentum?: Momentum;
};

export default function InsightsV2({ insightsV2, onSetFocus }: { insightsV2: InsightsV2Data; onSetFocus?: (messageId: string) => void }) {
  const actionPlan = insightsV2?.action_plan;
  const keyInsights = insightsV2?.key_insights || [];
  const experiment = insightsV2?.experiment;
  const framework = insightsV2?.framework;
  const momentum = insightsV2?.momentum;

  const chip = (t: 'up'|'down'|'steady') => t === 'up' ? 'text-emerald-700 bg-emerald-50' : t === 'down' ? 'text-rose-700 bg-rose-50' : 'text-slate-700 bg-slate-100';

  return (
    <div className="space-y-6">
      {/* SECTION 1: 7-Day Action Plan */}
      <section className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">This Week’s Primary Focus</h2>
            <p className="mt-2 text-slate-800 font-medium">
              {actionPlan?.primaryFocus?.label || 'Set your focus for this week.'}
            </p>
            <div className="mt-3 flex items-center gap-3">
              {actionPlan?.primaryFocus?.id && onSetFocus && (
                <button
                  type="button"
                  onClick={() => onSetFocus(actionPlan.primaryFocus!.id)}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  This is my current focus
                </button>
              )}
            </div>
          </div>
          <div className="min-w-[160px]">
            <p className="text-xs text-slate-500 mb-2">Progress</p>
            <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
              {(() => {
                const prog = actionPlan?.progress || { completed: 0, total: 0 };
                const pct = prog.total > 0 ? Math.min(100, Math.round((prog.completed / prog.total) * 100)) : 0;
                return <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />;
              })()}
            </div>
            <p className="mt-1 text-xs text-slate-600">
              {(() => {
                const prog = actionPlan?.progress || { completed: 0, total: 0 };
                return `${prog.completed}/${prog.total} completed`;
              })()}
            </p>
          </div>
        </div>
        <div className="mt-5">
          <p className="text-sm font-semibold text-slate-900">Supporting Actions (max 3)</p>
          <ul className="mt-2 space-y-1.5">
            {(actionPlan?.supporting || []).map((a) => (
              <li key={a.id} className="text-sm text-slate-700">• {a.label}</li>
            ))}
            {(actionPlan?.supporting || []).length === 0 && (
              <li className="text-sm text-slate-500">No supporting actions yet.</li>
            )}
          </ul>
          <p className="mt-4 text-sm text-slate-600">
            <span className="font-semibold text-slate-800">Reflection:</span> {actionPlan?.reflectionPrompt}
          </p>
        </div>
      </section>

      {/* SECTION 2: Key Insight (Simplified) */}
      <section className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
        <h2 className="font-display text-xl font-semibold text-slate-900">Key Insight</h2>
        <div className="mt-3 grid gap-3">
          {keyInsights.slice(0, 2).map((it, idx) => (
            <div key={idx} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-sm"><span className="font-semibold">Pattern:</span> {it.pattern}</p>
              <p className="text-sm"><span className="font-semibold">Impact:</span> {it.impact}</p>
              <p className="text-sm"><span className="font-semibold">Shift:</span> {it.shift}</p>
            </div>
          ))}
          {keyInsights.length === 0 && (
            <div className="text-sm text-slate-500">No insights yet. Generate a MoM to surface patterns.</div>
          )}
        </div>
      </section>

      {/* SECTION 3: Blindspot → Experiment */}
      <section className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
        <h2 className="font-display text-xl font-semibold text-slate-900">Experiment (30 Days)</h2>
        {experiment ? (
          <div className="mt-3 space-y-2 text-sm text-slate-800">
            <p><span className="font-semibold">Pattern Identified:</span> {experiment.patternIdentified}</p>
            <p><span className="font-semibold">Cost of Continuing:</span> {experiment.costOfContinuing}</p>
            <p><span className="font-semibold">30‑Day Experiment:</span> {experiment.experiment30d}</p>
          </div>
        ) : (
          <div className="text-sm text-slate-500">No experiment suggested yet.</div>
        )}
      </section>

      {/* SECTION 4: Apply This Framework */}
      <section className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
        <h2 className="font-display text-xl font-semibold text-slate-900">Apply This Framework</h2>
        {framework ? (
          <div className="mt-3 space-y-2 text-sm text-slate-800">
            <p><span className="font-semibold">When you feel:</span> {framework.when}</p>
            <p><span className="font-semibold">Do:</span> {framework.do}</p>
            <p><span className="font-semibold">Avoid:</span> {framework.avoid}</p>
          </div>
        ) : (
          <div className="text-sm text-slate-500">No framework recommendation yet.</div>
        )}
      </section>

      {/* SECTION 5: Momentum & Behavior Shift */}
      <section className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
        <h2 className="font-display text-xl font-semibold text-slate-900">Momentum & Behavior Shift</h2>
        {momentum ? (
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 ${chip(momentum.sinceLastMonth?.clarity || 'steady')}`}>Clarity {momentum.sinceLastMonth?.clarity === 'up' ? '↑' : momentum.sinceLastMonth?.clarity === 'down' ? '↓' : '→'}</span>
              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 ${chip(momentum.sinceLastMonth?.overwhelm || 'steady')}`}>Overwhelm {momentum.sinceLastMonth?.overwhelm === 'up' ? '↑' : momentum.sinceLastMonth?.overwhelm === 'down' ? '↓' : '→'}</span>
              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 ${chip(momentum.sinceLastMonth?.focus || 'steady')}`}>Focus {momentum.sinceLastMonth?.focus === 'up' ? '↑' : momentum.sinceLastMonth?.focus === 'down' ? '↓' : '→'}</span>
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 text-sm"><span className="font-semibold">Stopped:</span> {momentum.changes?.stopped || '—'}</div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 text-sm"><span className="font-semibold">Started:</span> {momentum.changes?.started || '—'}</div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 text-sm"><span className="font-semibold">Strengthening:</span> {momentum.changes?.strengthening || '—'}</div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-500">No momentum data yet.</div>
        )}
      </section>
    </div>
  );
}
